/** The Defaults tab's two reads, shared by the sections and the tab (which waits for both
 *  before it scrolls a deep-linked section into view) — one request each. */
import { useQuery } from '@tanstack/react-query';

import { ConsoleApiError } from '@/api/client';
import {
  defaultTagCategoriesKey,
  getDefaultTagCategories,
  getTripDefaults,
  tripDefaultsKey,
} from '@/api/tourOperatorDefaults';

export function useDefaultTagCategories(topId: number) {
  return useQuery({
    queryKey: defaultTagCategoriesKey(topId),
    queryFn: ({ signal }) => getDefaultTagCategories(topId, signal),
  });
}

export function useTripDefaults(topId: number) {
  return useQuery({
    queryKey: tripDefaultsKey(topId),
    queryFn: ({ signal }) => getTripDefaults(topId, signal),
  });
}

/** A failed request in words: the server's `detail`, else DRF's field errors ("reason:
 *  Ensure this field has at least 3 characters."), else the transport message. */
export function apiErrorText(e: unknown, fallback = 'The save failed.'): string {
  if (!(e instanceof ConsoleApiError)) return fallback;
  const body = e.body as Record<string, unknown> | undefined;
  if (body && typeof body.detail === 'string' && body.detail) return body.detail;
  if (body && typeof body === 'object') {
    const parts = Object.entries(body)
      .filter(([, v]) => Array.isArray(v) || typeof v === 'string')
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.map(String).join(' ') : String(v)}`);
    if (parts.length) return parts.join(' · ');
  }
  return e.message || fallback;
}
